import React from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { formatCalendarDate, formatUtcDate } from '../../utils/dateFormatUtils';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';

export const formatWaitlistPreference = (preference) => (
  `${formatCalendarDate(preference.date)} ${String(preference.from).slice(0, 5)}–${String(preference.to).slice(0, 5)}`
);

const DetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      py: 0.75,
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

export const WaitlistQueueEntryActions = ({ onBook, onRemove, fullWidth = false }) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction={fullWidth ? 'column' : 'row'}
      spacing={1}
      justifyContent="flex-end"
      sx={{ width: fullWidth ? '100%' : 'auto' }}
    >
      <PrimaryActionButton
        type="button"
        onClick={onBook}
        fullWidth={fullWidth}
        sx={fullWidth ? undefined : { minHeight: 36, px: 1.5 }}
      >
        {t('waitlistQueuePage.book')}
      </PrimaryActionButton>
      <Button
        type="button"
        color="error"
        variant={fullWidth ? 'outlined' : 'text'}
        onClick={onRemove}
        fullWidth={fullWidth}
        sx={{ fontWeight: 600, minHeight: fullWidth ? 44 : 36 }}
      >
        {t('waitlistQueuePage.remove')}
      </Button>
    </Stack>
  );
};

const WaitlistQueueEntryCard = ({ entry, currency, onBook, onRemove }) => {
  const { t } = useTranslation();
  const preferences = (entry.preferences || []).map(formatWaitlistPreference);

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 2,
        backgroundColor: '#fff',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 1 }}>
        {entry.customerName}
      </Typography>
      <Divider sx={{ mb: 0.5 }} />
      <DetailRow
        label={t('waitlistQueuePage.columns.services')}
        value={(entry.serviceNames || []).join(', ') || '—'}
      />
      <DetailRow
        label={t('waitlistQueuePage.columns.preferences')}
        value={preferences.length > 0 ? preferences.join(', ') : '—'}
      />
      <DetailRow
        label={t('waitlistQueuePage.columns.joined')}
        value={formatUtcDate(entry.createdAtUtc)}
      />
      <DetailRow
        label={t('waitlistQueuePage.columns.value')}
        value={formatCurrency(entry.estimatedAmountDue, currency || DEFAULT_CURRENCY)}
      />
      <Box sx={{ mt: 1.5 }}>
        <WaitlistQueueEntryActions onBook={onBook} onRemove={onRemove} fullWidth />
      </Box>
    </Box>
  );
};

export default WaitlistQueueEntryCard;
